import { clsx, type ClassValue } from 'clsx';
import { Box, Check, FileText, Layers } from 'lucide-react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Tone } from '../types';

/** Same helper both apps keep in `lib/utils`. Duplicated rather than imported —
 * a package can't reach into an app, and it is two lines. */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * The small marks the page is built from.
 *
 * Deliberately NOT a Button: every action on this page is a ReactNode slot the
 * app fills with its own button, because the app owns permissions, loading
 * states and what each control opens. What lives here is the handful of purely
 * visual pieces that carry the design's language — the card frame, the section
 * headings, the selection glyphs — none of which any app has a competing copy of.
 *
 * Sizes are the design's own pixels — the apps moved off the old 0.8 scale, so
 * an 18px gutter is `18px`.
 */

/* ────────────────────────── surfaces ────────────────────────── */

export function Card({
	children,
	className,
	padded,
}: {
	children: ReactNode;
	/** Adds the standard 14.4px gutter. Cards that own their own rows pass nothing. */
	padded?: boolean;
	className?: string;
}) {
	return (
		<div
			className={cn(
				'overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface',
				padded && 'p-4',
				className
			)}
		>
			{children}
		</div>
	);
}

/** The uppercase label used for column heads and small captions throughout. */
export function MicroLabel({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<span
			className={cn(
				'text-2xs font-medium uppercase tracking-[0.08em] text-text-4',
				className
			)}
		>
			{children}
		</span>
	);
}

/** The heading above each section of the page. */
export function SectionHead({
	title,
	meta,
	action,
}: {
	title: string;
	/** One line saying what the section is for. */
	meta?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<div className="mb-3 flex items-center justify-between gap-3">
			<div className="flex min-w-0 items-baseline gap-3">
				<h2 className="m-0 text-base font-medium uppercase tracking-[0.05em] text-text-1">
					{title}
				</h2>
				{meta && <span className="min-w-0 text-sm text-text-4">{meta}</span>}
			</div>
			{action && <div className="shrink-0">{action}</div>}
		</div>
	);
}

/** The header strip inside a card — one concern of the offer per card. */
export function SubHead({
	icon: Icon,
	title,
	hint,
	right,
}: {
	icon?: React.ElementType;
	title: string;
	hint?: ReactNode;
	right?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-center gap-2 border-b border-line-1 bg-surface px-4 py-3">
			{Icon && <Icon size={14} className="shrink-0 text-text-4" />}
			<MicroLabel className="text-text-2">{title}</MicroLabel>
			{hint && <span className="text-sm text-text-4">{hint}</span>}
			{right && <span className="ml-auto text-sm text-text-4">{right}</span>}
		</div>
	);
}

/* ────────────────────────── marks ────────────────────────── */

const TONE_CLASS: Record<Tone, string> = {
	neutral: 'bg-status-neutral-soft text-text-2',
	info: 'bg-status-info-soft text-status-info',
	success: 'bg-status-success-soft text-status-success',
	warn: 'bg-status-warn-soft text-status-warn',
	danger: 'bg-status-danger-soft text-status-danger',
	accent: 'bg-accent-soft text-accent',
};

export function Badge({
	tone = 'neutral',
	dot,
	children,
	className,
}: {
	tone?: Tone;
	dot?: boolean;
	children: ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				'inline-flex h-4 shrink-0 items-center gap-1.5 rounded-[var(--r-1)] px-1.5 text-2xs font-medium uppercase tracking-[0.02em]',
				TONE_CLASS[tone],
				className
			)}
		>
			{dot && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
			{children}
		</span>
	);
}

/**
 * Selection glyphs. Presentational only — every caller already has a clickable
 * row or pill around them, matching the Radio each app ships.
 *
 * Whole pixels: a fractional dot inside a fractional ring antialiases
 * unevenly and reads as off-centre however it is positioned.
 */
export function RadioMark({ on, muted }: { on: boolean; muted?: boolean }) {
	return (
		<span
			aria-hidden
			className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
			style={{
				boxShadow: `inset 0 0 0 1px ${
					on ? 'var(--accent)' : muted ? 'var(--line-2)' : 'var(--line-3)'
				}`,
			}}
		>
			{on && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
		</span>
	);
}

export function CheckMark({ on, muted }: { on: boolean; muted?: boolean }) {
	return (
		<span
			aria-hidden
			className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[var(--r-1)]"
			style={{
				background: on ? 'var(--accent)' : 'transparent',
				boxShadow: `inset 0 0 0 1px ${
					on ? 'var(--accent)' : muted ? 'var(--line-2)' : 'var(--line-3)'
				}`,
			}}
		>
			{on && <Check size={10} strokeWidth={3} className="text-accent-fg" />}
		</span>
	);
}

/** The accepted mark: a statement, not one option among several. */
export function AcceptedMark({ title }: { title?: string }) {
	return (
		<span
			title={title}
			className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent"
		>
			<Check size={9} strokeWidth={3} className="text-accent-fg" />
		</span>
	);
}

/** The product thumb well. Apps pass their own <img>; this is the fallback frame. */
export function Thumb({ size = 21, children }: { size?: number; children?: ReactNode }) {
	return (
		<span
			className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--r-2)] border border-line-2 bg-surface-fill text-text-4"
			style={{ width: size, height: size }}
		>
			{children ?? <Box size={size > 24 ? 12 : 10} />}
		</span>
	);
}

/**
 * One file, as a tile.
 *
 * The design's unit for a file that carries more than a name — the same pieces
 * the product page lists its assets with, turned on their side: a small thumb,
 * then the name, what it is, and what it is for. A strip of chips was the first
 * try and it could not hold the one thing that makes a file worth listing: a
 * quote file's description says WHICH placement the artwork is for and WHAT a
 * spec sheet covers, and in a chip that only ever fitted in a tooltip.
 *
 * Wide rather than tall, and the thumb small: the picture is an identifier here,
 * not the subject — these are tech packs and invoices, not photographs — so it
 * earns 40px, and the text gets the rest of the row. A column of portrait tiles
 * made the files the tallest thing on a card about a product.
 *
 * `onOpen` is optional on purpose: a file whose URL has not resolved yet still
 * gets a tile, it just isn't a button. Nothing silently goes missing from the
 * grid while an upload catches up.
 */
export function FileTile({
	name,
	meta,
	description,
	preview,
	onOpen,
	actions,
}: {
	name: string;
	/** Type and size, pre-formatted — "PDF · 1.2 MB". See `fileMeta`. */
	meta?: ReactNode;
	/** What the file is and why it is here. Two lines, then the tooltip. */
	description?: string | null;
	/** The picture, for a file that is one. Falls back to a file glyph. */
	preview?: ReactNode;
	onOpen?: () => void;
	/** Edit and delete, for the surface that owns the file. */
	actions?: ReactNode;
}) {
	const body = (
		<>
			<span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[var(--r-1)] border border-line-1 bg-surface-fill text-text-4">
				{preview ?? <FileText size={14} />}
			</span>
			<span className="flex min-w-0 flex-col text-left">
				<span className="min-w-0 truncate text-xs font-medium text-text-1">{name}</span>
				{meta && <span className="font-mono text-2xs text-text-4">{meta}</span>}
				{description && (
					<span className="mt-0.5 line-clamp-2 text-2xs leading-normal text-text-3">
						{description}
					</span>
				)}
			</span>
		</>
	);

	return (
		<span
			title={description ? `${name} — ${description}` : name}
			/* Top-aligned, not centred: the grid stretches every tile to the height
			   of the tallest in its row, and centring floated a file with no
			   description into the middle of its box while its neighbours started at
			   the top. The thumbs sit on one line across the row. */
			className="flex min-w-0 items-start gap-2.5 rounded-[var(--r-2)] border border-line-1 bg-surface p-2 transition-colors hover:border-line-2"
		>
			{onOpen ? (
				<button
					type="button"
					onClick={onOpen}
					className="flex min-w-0 flex-1 items-start gap-2.5 bg-transparent p-0"
				>
					{body}
				</button>
			) : (
				<span className="flex min-w-0 flex-1 items-start gap-2.5">{body}</span>
			)}
			{/* In flow at the end of the row, not revealed on hover: the row is wide
			    enough to hold them, and an invisible control that only exists once
			    the cursor finds it is worse than a muted one that is always there. */}
			{actions && <span className="flex shrink-0 items-center gap-0.5">{actions}</span>}
		</span>
	);
}

/**
 * The tile's meta line — "AI · 3.2 MB".
 *
 * The extension, not the mime type: an Illustrator file is an .ai to everyone
 * who works with one, and `application/postscript` to nobody. The mime subtype
 * is the fallback for a name with no extension.
 *
 * Here rather than in each app because both of them list the same files and a
 * second copy of this is a second answer to "what kind of file is that".
 */
export function fileMeta(name: string, mimeType: string, size: number) {
	const ext = /\.([a-z0-9]{1,5})$/i.exec(name)?.[1];
	const kind = (ext ?? mimeType.split('/')[1] ?? mimeType).toUpperCase();
	const bytes =
		size < 1024 * 1024
			? `${(size / 1024).toFixed(0)} KB`
			: `${(size / (1024 * 1024)).toFixed(1)} MB`;
	return `${kind} · ${bytes}`;
}

/**
 * The grid the tiles sit in. A fixed minimum rather than a column count: the
 * files section is as wide as the card, and the card is as wide as the page gave
 * it — one tile or nine, they are the same size.
 *
 * The minimum is set by the text, not the thumb: below about 260px the name
 * starts truncating before the description has said anything.
 */
export function FileGrid({ children }: { children: ReactNode }) {
	return (
		<div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]">
			{children}
		</div>
	);
}

/**
 * Horizontal scroll for content that has a minimum width.
 *
 * Deliberately NOT inside the tables themselves. A table knows its own minimum —
 * it knows how many columns it has and roughly what they need — but what should
 * happen when the page is narrower than that is the page's call: scroll
 * sideways, stack into cards, drop a column. The modules keep the minimum and
 * the page composes the strategy, so a surface that wants something other than
 * scrolling is not fighting the component to get it.
 */
export function ScrollX({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn('overflow-x-auto', className)}>{children}</div>;
}

/** A dashed placeholder row — an empty section says so rather than vanishing. */
export function EmptyRow({ children }: { children: ReactNode }) {
	return (
		<div className="rounded-[var(--r-2)] border border-dashed border-line-2 px-3 py-3 text-sm text-text-4">
			{children}
		</div>
	);
}

/**
 * The solid badge on a product a plan has taken over.
 *
 * Filled rather than outlined — the only filled badge in the offer — because it
 * is the one thing that changes what the rows beneath it mean.
 */
export function PinPill({ children }: { children: ReactNode }) {
	return (
		<span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-2 py-0.5 text-2xs font-semibold tracking-[0.08em] whitespace-nowrap text-[var(--canvas)] uppercase">
			<Layers size={9} />
			{children}
		</span>
	);
}
